import { ChangeDetectionStrategy, Component, input, model } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { UI } from '../ui';
import { ChipMultiSelectComponent } from './chip-multi-select.component';
import {
  FEE_BEARERS,
  PAYMENT_LINK_METHODS,
  type PaymentOptionsValue,
} from './merchant-config.models';

/** Fee bearer, checkout payment options and the payment-link methods switched off for the merchant. */
@Component({
  selector: 'mm-payment-options-config',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, ChipMultiSelectComponent, ...UI],
  template: `
    <div class="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
      <label class="flex flex-col gap-1 text-[0.8125rem] font-semibold text-fg">
        <span>Fees paid by (default)</span>
        <select
          uiInput
          [ngModel]="value().fee_bearer"
          (ngModelChange)="set('fee_bearer', $event)"
          [disabled]="disabled()"
        >
          @for (bearer of bearers; track bearer.value) {
            <option [value]="bearer.value">{{ bearer.label }}</option>
          }
        </select>
      </label>
    </div>
    <div class="mt-4 flex flex-col gap-3">
      @for (toggle of toggles; track toggle.key) {
        @if (toggle.key !== 'allow_merchant_fees' || showMerchantFees()) {
        <ui-switch
          [checked]="value()[toggle.key]"
          [disabled]="disabled()"
          (changed)="set(toggle.key, $event)"
        >
          {{ toggle.label }}
          <span description>{{ toggle.hint }}</span>
        </ui-switch>
        }
      }
    </div>
    <div class="mt-4">
      <p class="mb-1.5 text-[0.8125rem] font-semibold text-fg">Payment-link methods disabled</p>
      <mm-chip-multi-select
        label="Disabled payment-link methods"
        [options]="methods"
        [disabled]="disabled()"
        [value]="value().payment_link_disabled_methods"
        (valueChange)="set('payment_link_disabled_methods', $event)"
      />
      <p class="mt-1 text-xs text-muted">Selected methods are not offered on this merchant's payment links.</p>
    </div>
  `,
})
export class PaymentOptionsConfigComponent {
  readonly value = model.required<PaymentOptionsValue>();
  readonly disabled = input(false);
  /** The hub edits `allow_merchant_fees` on the Portal page, so it can hide it here. */
  readonly showMerchantFees = input(true);

  readonly bearers = FEE_BEARERS;
  readonly methods = PAYMENT_LINK_METHODS;
  readonly toggles = [
    { key: 'allow_merchant_fees', label: 'Allow merchant fees', hint: 'Let the merchant set its own fees.' },
    { key: 'checkout_card_enabled', label: 'Card payments in checkout', hint: 'Needs a card provider integration.' },
    { key: 'checkout_mainmoney_enabled', label: 'MainMoney wallet payments in checkout', hint: '' },
  ] as const;

  set<K extends keyof PaymentOptionsValue>(key: K, next: PaymentOptionsValue[K]): void {
    this.value.update((current) => ({ ...current, [key]: next }));
  }
}
